/**
 * CollapsedGroupsLayer — renders GroupInstances as static SVG with
 * per-atom/bond hover highlighting (PIPH-77).
 *
 * Each instance renders as one `<g>` wrapping pre-computed atom/bond/electron/charge
 * SVG from the template. No DynamicAtom, no MotionValues, no per-atom React hooks —
 * one React component per instance instead of one per atom.
 *
 * ## Hit testing
 *
 * Each instance `<g>` has pointer event handlers that do JS-based coordinate
 * lookup to identify which template atom/bond the pointer is nearest to:
 *
 *   1. Convert screen pixel to SVG coordinates via getScreenCTM()
 *   2. Apply inverse instance transform to get template-local coordinates
 *   3. Walk template atoms/bonds (typically 12-16), find nearest within hit radius
 *   4. Construct compound ID → feed to selection system as hovered
 */

import React, { useMemo, memo, useCallback, useState } from 'react';
import { useMolecularSVG } from '../../contexts/MolecularSVGContext';
import { useSVGRef } from '../../contexts/SVGRefContext';
import { getFormat } from '../../rendering/utils/formatContext';
import type { GroupInstance, Template, Transform2D, InstanceOverrides } from '@alchemiesolns/molecular-workspace';
import { makeCompoundId } from '@alchemiesolns/molecular-workspace';
import { buildRenderContainer } from '../../rendering/buildRenderContainer';
import { generateBondRecipes } from '../../rendering/bondRecipes';
import { generateAtomRecipes } from '../../rendering/atomRecipes';
import { Rings } from '@alchemiesolns/atomcontainer';
import { VisibilityFlag } from '@alchemiesolns/visibility-config';
import { computeBondEndPoints, computeLineAngleBondEndPoints } from '@alchemiesolns/bond-calc-pure';
import { svgPaperAtoms } from '../../rendering/svgPaperAtoms';
import { svgElectrons } from '../../rendering/svgElectrons';
import { svgElements, resetLonePairIndices } from '../../rendering/svgElements';
import { svgCharge } from '../../rendering/svgCharge';
import { useSelection } from '../../selection';

// ----- Hit testing types ---------------------------------------------------

interface HitTarget {
  localId: string;
  /** Template-local chemistry coordinates (y-up) */
  x: number;
  y: number;
}

interface BondHitTarget {
  localId: string;
  x1: number; y1: number;
  x2: number; y2: number;
}

interface HitResult {
  type: 'atom' | 'bond';
  localId: string;
}

// ----- Hit testing utilities -----------------------------------------------

function buildHitTargets(template: Template): HitTarget[] {
  const ws = template.workspace;
  const targets: HitTarget[] = [];
  for (const id of ws.getLooseAtomIds()) {
    if (ws.getVisibility(id) === VisibilityFlag.Hidden) continue;
    const atom = ws.getAtom(id);
    if (atom) targets.push({ localId: id, x: atom.x, y: atom.y });
  }
  return targets;
}

function buildBondHitTargets(template: Template): BondHitTarget[] {
  const ws = template.workspace;
  const targets: BondHitTarget[] = [];
  for (const id of ws.getLooseBondIds()) {
    const bond = ws.getBond(id);
    if (!bond) continue;
    if (ws.getVisibility(bond.a1.id) === VisibilityFlag.Hidden) continue;
    if (ws.getVisibility(bond.a2.id) === VisibilityFlag.Hidden) continue;
    targets.push({ localId: id, x1: bond.a1.x, y1: bond.a1.y, x2: bond.a2.x, y2: bond.a2.y });
  }
  return targets;
}

function pointToSegmentDistSq(
  px: number, py: number,
  x1: number, y1: number, x2: number, y2: number,
): number {
  const dx = x2 - x1, dy = y2 - y1;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = px - x1, ey = py - y1;
    return ex * ex + ey * ey;
  }
  const t = Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / lenSq));
  const projX = x1 + t * dx, projY = y1 + t * dy;
  const ex = px - projX, ey = py - projY;
  return ex * ex + ey * ey;
}

/**
 * Three-pass hit test: tight atom → bond → wide atom.
 * Coordinates are in template-local chemistry space (y-up).
 */
function findNearestElement(
  localX: number, localY: number,
  atomTargets: HitTarget[], bondTargets: BondHitTarget[],
  atomHitRadius: number,
): HitResult | null {
  const tightRadius = atomHitRadius * 0.4;
  const tightRadiusSq = tightRadius * tightRadius;
  const wideRadiusSq = atomHitRadius * atomHitRadius;
  const bondDistThreshold = atomHitRadius * 0.6;
  const bondDistThresholdSq = bondDistThreshold * bondDistThreshold;

  // Pass 1: tight atom check
  let bestAtomDistSq = Infinity;
  let bestAtom: HitTarget | null = null;
  for (const t of atomTargets) {
    const dx = localX - t.x, dy = localY - t.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < tightRadiusSq && distSq < bestAtomDistSq) {
      bestAtomDistSq = distSq;
      bestAtom = t;
    }
  }
  if (bestAtom) return { type: 'atom', localId: bestAtom.localId };

  // Pass 2: bond check
  let bestBondDistSq = Infinity;
  let bestBond: BondHitTarget | null = null;
  for (const b of bondTargets) {
    const distSq = pointToSegmentDistSq(localX, localY, b.x1, b.y1, b.x2, b.y2);
    if (distSq < bondDistThresholdSq && distSq < bestBondDistSq) {
      bestBondDistSq = distSq;
      bestBond = b;
    }
  }
  if (bestBond) return { type: 'bond', localId: bestBond.localId };

  // Pass 3: wide atom fallback
  bestAtomDistSq = Infinity;
  bestAtom = null;
  for (const t of atomTargets) {
    const dx = localX - t.x, dy = localY - t.y;
    const distSq = dx * dx + dy * dy;
    if (distSq < wideRadiusSq && distSq < bestAtomDistSq) {
      bestAtomDistSq = distSq;
      bestAtom = t;
    }
  }
  if (bestAtom) return { type: 'atom', localId: bestAtom.localId };

  return null;
}

/**
 * Convert SVG coordinates to template-local chemistry coordinates
 * by undoing the instance transform (un-translate, un-y-flip, un-rotate).
 */
function svgToTemplateLocal(
  svgX: number, svgY: number, transform: Transform2D,
): { x: number; y: number } {
  // SVG → world: undo translate, undo y-flip
  const wx = svgX - transform.tx;
  const wy = -(svgY - (-transform.ty)); // undo y-negate: svgY = -ty, so worldY = -(svgY + ty)

  // World → template-local: undo rotation
  if (transform.rotation === 0) return { x: wx, y: wy };
  const cos = Math.cos(-transform.rotation);
  const sin = Math.sin(-transform.rotation);
  return { x: wx * cos - wy * sin, y: wx * sin + wy * cos };
}

// ----- Template SVG pre-computation ----------------------------------------

interface TemplateBounds {
  minX: number; minY: number; maxX: number; maxY: number;
}

interface TemplateSVGData {
  atomElements: React.ReactNode[];
  bondElements: React.ReactNode[];
  electronElements: React.ReactNode[];
  chargeElements: React.ReactNode[];
  atomHitTargets: HitTarget[];
  bondHitTargets: BondHitTarget[];
  bounds: TemplateBounds;
}

function computeTemplateSVG(template: Template): TemplateSVGData {
  const ws = template.workspace;
  const container = buildRenderContainer(ws);
  const format = getFormat();

  // Build visibility defs and atom-to-index map
  const visDefs: import('@alchemiesolns/visibility-config').VisibilityConfigDef[] = [];
  const atomIdToContainerIndex = new Map<string, number>();
  container.atoms.forEach((atom, i) => { atomIdToContainerIndex.set(atom.id!, i); });

  for (const id of ws.getLooseAtomIds()) {
    const vis = ws.getVisibility(id);
    const idx = atomIdToContainerIndex.get(id) ?? -1;
    if (idx !== -1 && vis !== undefined && vis !== VisibilityFlag.Visible) {
      visDefs.push({ a: idx, visibilityFlag: vis as any });
    }
  }

  const visDefByIndex = new Map<number, import('@alchemiesolns/visibility-config').VisibilityConfigDef>();
  for (const v of visDefs) visDefByIndex.set(v.a, v);

  const visibleAtoms = container.atoms.filter(a => {
    const idx = atomIdToContainerIndex.get(a.id!) ?? -1;
    const visDef = visDefByIndex.get(idx);
    return visDef?.visibilityFlag !== VisibilityFlag.Hidden;
  });

  // Atoms
  generateAtomRecipes(container.atoms, container, visDefs, visibleAtoms);
  const atomElements: React.ReactNode[] = container.atoms.map((a) => {
    const idx = atomIdToContainerIndex.get(a.id!) ?? -1;
    const visDef = visDefByIndex.get(idx);
    if (visDef?.visibilityFlag === VisibilityFlag.Hidden) return null;

    const graphics = svgPaperAtoms.drawAtom(a, container, visibleAtoms, visDefs);
    return (
      <g key={`atom-${a.id}`} transform={`translate(${a.pos!.x}, ${a.pos!.y})`}
         style={{ pointerEvents: 'none' }}>
        {graphics}
      </g>
    );
  }).filter(Boolean);

  // Bonds
  const rings = Rings.getRingBonds(container);
  const ringBonds = Array.from(new Set(rings.flat()));
  const bondRecipes = generateBondRecipes(container.bonds, ringBonds, container, visDefs);

  const bondElements: React.ReactNode[] = bondRecipes.map((recipe) => {
    if (recipe.visibility === 'hidden') return null;
    return recipe.tubes.map((tube) => {
      const a1 = container.atoms.find(a => a.id === recipe.atom1Id);
      const a2 = container.atoms.find(a => a.id === recipe.atom2Id);
      if (!a1?.pos || !a2?.pos) return null;

      const a1Pos = [a1.pos.x, a1.pos.y, 0];
      const a2Pos = [a2.pos.x, a2.pos.y, 0];
      const offsetEndPoints = computeBondEndPoints(a1Pos, a2Pos, [0, 0, 1], tube.offset);
      const finalEndPoints = computeLineAngleBondEndPoints(
        recipe.element1, recipe.element2,
        offsetEndPoints.a1, offsetEndPoints.a2,
        recipe.atom1NeighborCount, recipe.atom2NeighborCount,
        recipe.order, tube.lengthFactor, tube.lineIndex, tube.centered,
        format, recipe.atom1VisibilityFlag, recipe.atom2VisibilityFlag,
      );
      return (
        <line
          key={`bond-${recipe.bondId}-${tube.lineIndex}`}
          x1={finalEndPoints.a1[0]} y1={finalEndPoints.a1[1]}
          x2={finalEndPoints.a2[0]} y2={finalEndPoints.a2[1]}
          stroke="black" strokeWidth={format.bond.thickness}
          style={{ pointerEvents: 'none' }}
        />
      );
    });
  }).flat().filter(Boolean);

  // Electrons (lone pairs + radicals)
  resetLonePairIndices();
  const lps = container.atoms.filter(a => {
    if (a.lonePairs === 0) return false;
    const idx = atomIdToContainerIndex.get(a.id!) ?? -1;
    const visDef = visDefByIndex.get(idx);
    return visDef?.visibilityFlag !== VisibilityFlag.HiddenLP &&
           visDef?.visibilityFlag !== VisibilityFlag.ForceTextHiddenLP;
  });
  const rads = container.atoms.filter(a => a.radicals > 0);

  const visibleBondsForElectrons = container.bonds.filter(bond => {
    const a1Idx = atomIdToContainerIndex.get(bond.a1.id!) ?? -1;
    const a2Idx = atomIdToContainerIndex.get(bond.a2.id!) ?? -1;
    const v1 = visDefByIndex.get(a1Idx);
    const v2 = visDefByIndex.get(a2Idx);
    return v1?.visibilityFlag !== VisibilityFlag.Hidden &&
           v2?.visibilityFlag !== VisibilityFlag.Hidden;
  });

  const hydrogenDirections = container.atoms.map((a) => ({
    id: a.id!,
    dir: svgPaperAtoms.calcHydrogenDirection(a, container, visibleAtoms),
  }));

  const electronCoords = svgElectrons.calcElectronPositions(
    lps, rads, visibleBondsForElectrons, hydrogenDirections, container, visibleAtoms
  );

  const electronElements: React.ReactNode[] = electronCoords.map((coord, i) =>
    <g key={`electron-${i}`} style={{ pointerEvents: 'none' }}>
      {svgElements.electrons(coord, container, visDefs)}
    </g>
  );

  // Charges
  const chargePositions: any[] = [];
  const chargeCoords = svgCharge.calcChargeCoords(container, chargePositions, format.charge.radius, visibleAtoms);
  const chargeElements: React.ReactNode[] = chargeCoords.map((coord, i) =>
    <g key={`charge-${i}`} style={{ pointerEvents: 'none' }}>
      {svgElements.charge(coord, i)}
    </g>
  );

  // Hit targets
  const atomHitTargets = buildHitTargets(template);
  const bondHitTargets = buildBondHitTargets(template);

  // Template bounds in SVG coords (y-flipped) for hit rect sizing
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const t of atomHitTargets) {
    const svgY = -t.y;
    if (t.x < minX) minX = t.x;
    if (t.x > maxX) maxX = t.x;
    if (svgY < minY) minY = svgY;
    if (svgY > maxY) maxY = svgY;
  }
  const bounds: TemplateBounds = { minX, minY, maxX, maxY };

  return { atomElements, bondElements, electronElements, chargeElements, atomHitTargets, bondHitTargets, bounds };
}

/**
 * Slow path: compute SVG for an instance that has overrides.
 * Applies overrides to a copy of the template workspace, then renders.
 */
function computeInstanceSVG(
  template: Template,
  overrides: InstanceOverrides | undefined,
): TemplateSVGData {
  if (!overrides) return computeTemplateSVG(template);

  let ws = template.workspace;

  if (overrides.visibility) {
    for (const [localId, flag] of overrides.visibility) {
      ws = ws.setVisibility(localId, flag);
    }
  }

  return computeTemplateSVG({ ...template, workspace: ws });
}

// ----- Hover highlight rendering -------------------------------------------

const HOVER_COLOR = '#3479F6';
const HOVER_STROKE = '#096D99';
const HOVER_OPACITY = 0.3;

interface HoverHighlightProps {
  hit: HitResult;
  template: Template;
}

function HoverHighlight({ hit, template }: HoverHighlightProps) {
  const format = getFormat();
  const ws = template.workspace;

  if (hit.type === 'atom') {
    const atom = ws.getAtom(hit.localId);
    if (!atom) return null;
    return (
      <circle
        cx={atom.x} cy={-atom.y}
        r={format.atom.radius * 1.8}
        fill={HOVER_COLOR} fillOpacity={HOVER_OPACITY}
        stroke={HOVER_STROKE} strokeWidth={0.3}
        style={{ pointerEvents: 'none' }}
      />
    );
  }

  // Bond highlight
  const bond = ws.getBond(hit.localId);
  if (!bond) return null;
  return (
    <line
      x1={bond.a1.x} y1={-bond.a1.y}
      x2={bond.a2.x} y2={-bond.a2.y}
      stroke={HOVER_COLOR}
      strokeWidth={format.bond.thickness * 3}
      strokeOpacity={HOVER_OPACITY + 0.1}
      strokeLinecap="round"
      style={{ pointerEvents: 'none' }}
    />
  );
}

// ----- Instance rendering --------------------------------------------------

interface CollapsedInstanceProps {
  instance: GroupInstance;
  templateSVG: TemplateSVGData;
  template: Template;
  svgElement: SVGSVGElement | null;
  hoveredHit: HitResult | null;
  onHover: (hit: HitResult | null, instanceId: string) => void;
  /** Called on click or double-click. Includes the hit local atom/bond ID and whether it was a double-click. */
  onActivate?: (instanceId: string, hitLocalId: string | null, isDoubleClick: boolean) => void;
  /** Called on pointer-down — lets client apps start drag-to-bond from collapsed atoms. */
  onAtomPointerDown?: (instanceId: string, hitLocalId: string | null, event: React.PointerEvent) => void;
  /** Forwarded pointer-move/up for drag tracking after pointer capture. */
  onForwardedPointerMove?: (event: React.PointerEvent) => void;
  onForwardedPointerUp?: (event: React.PointerEvent) => void;
}

const CollapsedInstance = memo(function CollapsedInstance({
  instance,
  templateSVG,
  template,
  svgElement,
  hoveredHit,
  onHover,
  onActivate,
  onAtomPointerDown,
  onForwardedPointerMove,
  onForwardedPointerUp,
}: CollapsedInstanceProps) {
  const { tx, ty, rotation } = instance.transform;
  const rotDeg = -rotation * (180 / Math.PI);
  const hasRotation = rotation !== 0;
  const format = getFormat();
  const atomHitRadius = format.atom.radius * 2.5;

  // Hit rect covers template bounds + padding for hit detection
  const { bounds } = templateSVG;
  const pad = atomHitRadius;
  const hitX = bounds.minX - pad;
  const hitY = bounds.minY - pad;
  const hitW = (bounds.maxX - bounds.minX) + pad * 2;
  const hitH = (bounds.maxY - bounds.minY) + pad * 2;

  const handlePointerMove = useCallback((e: React.PointerEvent) => {
    if (!svgElement) return;
    const ctm = svgElement.getScreenCTM();
    if (!ctm) return;

    // Screen → SVG coords
    const svgX = (e.clientX - ctm.e) / ctm.a;
    const svgY = (e.clientY - ctm.f) / ctm.d;

    // SVG → template-local
    const local = svgToTemplateLocal(svgX, svgY, instance.transform);

    const hit = findNearestElement(
      local.x, local.y,
      templateSVG.atomHitTargets, templateSVG.bondHitTargets,
      atomHitRadius,
    );
    onHover(hit, instance.id);
  }, [svgElement, instance.transform, instance.id, templateSVG.atomHitTargets, templateSVG.bondHitTargets, atomHitRadius, onHover]);

  const handlePointerLeave = useCallback(() => {
    onHover(null, instance.id);
  }, [onHover, instance.id]);

  const hitTestFromEvent = useCallback((e: React.MouseEvent): string | null => {
    if (!svgElement) return null;
    const ctm = svgElement.getScreenCTM();
    if (!ctm) return null;
    const svgX = (e.clientX - ctm.e) / ctm.a;
    const svgY = (e.clientY - ctm.f) / ctm.d;
    const local = svgToTemplateLocal(svgX, svgY, instance.transform);
    const hit = findNearestElement(
      local.x, local.y,
      templateSVG.atomHitTargets, templateSVG.bondHitTargets,
      atomHitRadius,
    );
    return hit?.localId ?? null;
  }, [svgElement, instance.transform, templateSVG.atomHitTargets, templateSVG.bondHitTargets, atomHitRadius]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onActivate?.(instance.id, hitTestFromEvent(e), false);
  }, [instance.id, hitTestFromEvent, onActivate]);

  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onActivate?.(instance.id, hitTestFromEvent(e), true);
  }, [instance.id, hitTestFromEvent, onActivate]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    onAtomPointerDown?.(instance.id, hitTestFromEvent(e), e);
  }, [instance.id, hitTestFromEvent, onAtomPointerDown]);

  const transformStr = hasRotation
    ? `translate(${tx}, ${-ty}) rotate(${rotDeg})`
    : `translate(${tx}, ${-ty})`;

  return (
    <g
      data-instance-id={instance.id}
      data-template-id={instance.templateId}
      transform={transformStr}
      onPointerDown={onAtomPointerDown ? handlePointerDown : undefined}
      onPointerMove={(e: React.PointerEvent) => { handlePointerMove(e); onForwardedPointerMove?.(e); }}
      onPointerUp={onForwardedPointerUp}
      onPointerLeave={handlePointerLeave}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Transparent hit rect so pointer events fire even between atoms */}
      <rect
        x={hitX} y={hitY} width={hitW} height={hitH}
        fill="transparent"
        style={{ cursor: 'default' }}
      />
      {templateSVG.bondElements}
      {templateSVG.atomElements}
      {templateSVG.electronElements}
      {templateSVG.chargeElements}
      {/* Invisible per-atom hit circles with compound-ID data-atom-id.
          Enables useDragToBond target detection via elementsFromPoint(). */}
      {templateSVG.atomHitTargets.map(target => (
        <circle
          key={`hit-${target.localId}`}
          data-atom-id={makeCompoundId(instance.id, target.localId)}
          cx={target.x}
          cy={-target.y}
          r={atomHitRadius * 0.5}
          fill="transparent"
          style={{ pointerEvents: 'auto' }}
        />
      ))}
      {hoveredHit && (
        <HoverHighlight hit={hoveredHit} template={template} />
      )}
    </g>
  );
});

// ----- Layer component -----------------------------------------------------

export interface CollapsedGroupsLayerProps {
  /** Called on click/double-click — expand instance and optionally select the hit atom */
  onActivate?: (instanceId: string, hitLocalId: string | null, isDoubleClick: boolean) => void;
  /** Called on pointer-down — lets client apps start drag-to-bond from collapsed atoms. */
  onAtomPointerDown?: (instanceId: string, hitLocalId: string | null, event: React.PointerEvent) => void;
  /** Forwarded pointer-move for drag tracking (e.g., useDragToBond.moveDrag). */
  onPointerMove?: (event: React.PointerEvent) => void;
  /** Forwarded pointer-up for drag tracking (e.g., useDragToBond.endDrag). */
  onPointerUp?: (event: React.PointerEvent) => void;
}

export const CollapsedGroupsLayer = memo(function CollapsedGroupsLayer({
  onActivate,
  onAtomPointerDown,
  onPointerMove: forwardedPointerMove,
  onPointerUp: forwardedPointerUp,
}: CollapsedGroupsLayerProps) {
  const { workspace } = useMolecularSVG();
  const svgRefCtx = useSVGRef({ optional: true });
  const selection = useSelection();
  const instances = workspace.getGroupInstances();
  const templates = workspace.getTemplates();

  const [hoveredState, setHoveredState] = useState<{
    instanceId: string;
    hit: HitResult;
  } | null>(null);

  const templateMap = useMemo(() => {
    const map = new Map<string, Template>();
    for (const tpl of templates) map.set(tpl.id, tpl);
    return map;
  }, [templates]);

  // Pre-compute SVG for each template (fast path) and each instance with
  // overrides (slow path). Both are memoized together so hover re-renders
  // don't re-run computeInstanceSVG on every frame.
  const { templateSVGCache, instanceSVGCache } = useMemo(() => {
    const tplCache = new Map<string, TemplateSVGData>();
    for (const tpl of templates) {
      tplCache.set(tpl.id, computeTemplateSVG(tpl));
    }
    const instCache = new Map<string, TemplateSVGData>();
    for (const inst of instances) {
      if (inst.overrides) {
        const tpl = templateMap.get(inst.templateId);
        if (tpl) {
          instCache.set(inst.id, computeInstanceSVG(tpl, inst.overrides));
        }
      }
    }
    return { templateSVGCache: tplCache, instanceSVGCache: instCache };
  }, [templates, instances, templateMap]);

  const handleHover = useCallback((hit: HitResult | null, instanceId: string) => {
    if (hit) {
      setHoveredState({ instanceId, hit });
      selection?.setHovered(makeCompoundId(instanceId, hit.localId));
    } else {
      setHoveredState(null);
      selection?.setHovered(null);
    }
  }, [selection]);

  // Connective bonds (bonds between collapsed instances and loose atoms) are
  // now rendered by BondLayer/DynamicBond — buildRenderContainer includes
  // compound-ID atoms from instances so bonds resolve both endpoints.

  if (instances.length === 0) return null;

  const svgElement = svgRefCtx?.svgRef?.current ?? null;

  return (
    <g data-layer="collapsed-groups">
      {instances.map((instance) => {
        const tpl = templateMap.get(instance.templateId);
        if (!tpl) return null;
        // Fast path: cached template SVG. Slow path: cached per-instance SVG.
        const svg = instanceSVGCache.get(instance.id)
          ?? templateSVGCache.get(instance.templateId);
        if (!svg) return null;

        const isHovered = hoveredState?.instanceId === instance.id;

        return (
          <CollapsedInstance
            key={instance.id}
            instance={instance}
            templateSVG={svg}
            template={tpl}
            svgElement={svgElement}
            hoveredHit={isHovered ? hoveredState!.hit : null}
            onHover={handleHover}
            onActivate={onActivate}
            onAtomPointerDown={onAtomPointerDown}
            onForwardedPointerMove={forwardedPointerMove}
            onForwardedPointerUp={forwardedPointerUp}
          />
        );
      })}
    </g>
  );
});
